import { z } from 'zod';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { Money } from './metric';
import { ContractStanding, EntityRef, OwnerRef } from './read-models';

/**
 * Contracts, obligations and renewal exposure — cluster C15.
 *
 * `/v2/contracts` was the only screen in the product that rendered a wholly
 * empty state, and it was right to: `core.contract` held no rows and its own
 * header comment explained that dressing supplier spend up as contract coverage
 * would tell a reader a renewal calendar existed when none did.
 *
 * These are the shapes that make the screen honest in the other direction.
 * Four rules live in the types rather than in the route.
 *
 * **A contract with no value is not a contract worth nothing.** `annualValue`
 * is nullable and `valueBasis` says why it is absent. Six of Aurora's
 * forty-seven agreements are frameworks whose money sits on the call-offs
 * beneath them; rendering them at zero would understate the sourcing book by
 * exactly the amount nobody has recorded.
 *
 * **A contract with no notice period cannot say when the decision is due.**
 * `noticeDeadline` is derived as `endDate - noticePeriodDays` and is null when
 * either side of that is missing. The screen shows "not recorded" and names
 * what would turn it on — it does not quietly fall back to the expiry, which
 * would tell a sourcing manager they had six months when they had none.
 *
 * **An expired contract stays in the register.** `standing` carries `Expired`
 * as a first-class value. Two of Aurora's agreements ran out and are still
 * being invoiced against, and a renewal calendar that hid them would be worse
 * than no calendar.
 *
 * **A supplier with no contract is a finding, not a gap in the data.**
 * `SupplierWithoutContract` is part of this view rather than an omission from
 * it: three suppliers take real money against no recorded agreement, and that
 * is the first thing a sourcing review wants to see.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 1. The register — /v2/contracts
// ─────────────────────────────────────────────────────────────────────────────

/** Why a contract carries no annual value. */
export const ValueBasis = z.enum(['Recorded', 'FrameworkNoCommittedValue', 'NotRecorded']);
export type ValueBasis = z.infer<typeof ValueBasis>;

export const ContractObligationRow = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  /** Service level, Compliance, Exit, Commercial, … as the agreement frames it. */
  obligationType: z.string().nullable(),
  /**
   * The standard this obligation has to meet, in the agreement's own words.
   *
   * Null where the agreement states an obligation without a measurable
   * standard — a review meeting, a named contact, reasonable endeavours. Those
   * are real obligations and they are not measurable, and a register that
   * showed the two the same way would be useless for deciding which ones can
   * be reported on.
   */
  serviceLevel: z.string().nullable(),
  frequency: z.string().nullable(),
  dueDate: IsoDate.nullable(),
  owner: OwnerRef.nullable(),
});
export type ContractObligationRow = z.infer<typeof ContractObligationRow>;

export const ContractRow = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  supplier: EntityRef,
  supplierRiskStatus: z.string().nullable(),
  contractType: z.string().nullable(),
  startDate: IsoDate,
  /** Null where the agreement rolls until cancelled. Read with `standing`. */
  endDate: IsoDate.nullable(),
  annualValue: Money.nullable(),
  valueBasis: ValueBasis,
  noticePeriodDays: z.number().int().positive().nullable(),
  /**
   * The last day on which Aurora can still walk away, derived from the expiry
   * and the notice period. Null when either is missing — never the expiry.
   */
  noticeDeadline: IsoDate.nullable(),
  /** Null where nobody recorded whether the agreement rolls. Not false. */
  autoRenews: z.boolean().nullable(),
  standing: ContractStanding,
  /** Days until expiry; negative for an agreement already past it. Null when evergreen. */
  daysToExpiry: z.number().int().nullable(),
  applicationsCovered: z.number().int().nonnegative(),
  productsSupported: z.number().int().nonnegative(),
  obligationCount: z.number().int().nonnegative(),
});
export type ContractRow = z.infer<typeof ContractRow>;

/**
 * One band of the renewal calendar.
 *
 * `value` is the total annual value of the agreements in the window and is
 * nullable: a window whose agreements all record no value has no total, which
 * is not the same as a window worth nothing. `valueUnknownCount` says how many
 * of the agreements in the band contributed nothing to the figure, so a reader
 * can see how complete it is before quoting it.
 */
export const RenewalWindow = z.object({
  label: z.string(),
  /** Days from today, inclusive lower bound. Negative for the expired band. */
  fromDays: z.number().int(),
  /** Days from today, exclusive upper bound. Null for the open-ended band. */
  toDays: z.number().int().nullable(),
  contractCount: z.number().int().nonnegative(),
  value: Money.nullable(),
  valueUnknownCount: z.number().int().nonnegative(),
  contracts: z.array(EntityRef),
});
export type RenewalWindow = z.infer<typeof RenewalWindow>;

/**
 * A supplier taking money with no agreement behind it.
 *
 * Spend is nullable for the same reason every figure in this product is: a
 * supplier with no recorded spend is not a supplier that costs nothing.
 */
export const SupplierWithoutContract = z.object({
  supplier: EntityRef,
  category: z.string().nullable(),
  riskStatus: z.string().nullable(),
  recordedSpend: Money.nullable(),
});
export type SupplierWithoutContract = z.infer<typeof SupplierWithoutContract>;

/**
 * How committed value compares with what was actually spent.
 *
 * Deliberately not a variance. `fact.supplier_spend` records what left the bank
 * against a supplier; `core.contract.annual_value` records what an agreement
 * commits to annually. They measure different things over different
 * populations — spend includes work outside any agreement, and commitment
 * includes agreements drawn down in a later period — so the two are reported
 * side by side with the coverage between them, and the product never subtracts
 * one from the other.
 */
export const CommitmentAgainstSpend = z.object({
  supplier: EntityRef,
  committedAnnualValue: Money.nullable(),
  recordedSpend: Money.nullable(),
  contractCount: z.number().int().nonnegative(),
  /** Of this supplier's agreements, how many record no value at all. */
  contractsWithoutValue: z.number().int().nonnegative(),
});
export type CommitmentAgainstSpend = z.infer<typeof CommitmentAgainstSpend>;

export const ContractRegisterView = z.object({
  contracts: z.array(ContractRow),
  /** Expired, then 0–2, 2–4, 4–12 months, then beyond. Always all five bands. */
  renewalWindows: z.array(RenewalWindow),
  /** Obligations for the selected contract only — the register does not carry them all. */
  selectedContractId: Uuid.nullable(),
  obligations: z.array(ContractObligationRow),
  suppliersWithoutContract: z.array(SupplierWithoutContract),
  commitmentAgainstSpend: z.array(CommitmentAgainstSpend),
  /** Contracts recorded, over suppliers recorded. 0–1. */
  supplierCoverage: z.number().min(0).max(1),
  /**
   * Total committed annual value across every agreement that records one, and
   * the count of agreements that record none.
   */
  committedAnnualValue: Money.nullable(),
  contractsWithoutValue: z.number().int().nonnegative(),
  asOf: IsoDate,
  refreshedAt: Timestamp,
});
export type ContractRegisterView = z.infer<typeof ContractRegisterView>;

// ─────────────────────────────────────────────────────────────────────────────
// 2. What a renewal decision looks like
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The four things a sourcing manager can say about an agreement coming up.
 *
 * `Renew`, `Renegotiate`, `Lapse` and `Replace` — and nothing else. The
 * temptation is a free-text status; the reason it is an enum is that a renewal
 * calendar's whole value is being able to count what is decided against what is
 * not, and free text cannot be counted.
 */
export const RenewalIntent = z.enum(['Renew', 'Renegotiate', 'Lapse', 'Replace']);
export type RenewalIntent = z.infer<typeof RenewalIntent>;

export const RenewalDecisionInput = z.object({
  intent: RenewalIntent,
  /**
   * Why. At least ten characters, refused rather than defaulted — a renewal
   * decision that carries no reason is unreadable to whoever inherits it.
   */
  rationale: z.string().min(10),
});
export type RenewalDecisionInput = z.infer<typeof RenewalDecisionInput>;

export const RenewalDecisionRecorded = z.object({
  contractId: Uuid,
  decisionId: Uuid,
  intent: RenewalIntent,
  rationale: z.string(),
  decidedBy: OwnerRef,
  decidedAt: Timestamp,
});
export type RenewalDecisionRecorded = z.infer<typeof RenewalDecisionRecorded>;
